import React, { useEffect, useState, useMemo } from "react";
import { MaterialReactTable } from 'material-react-table';
import { Box, Typography, TextField} from '@mui/material';
import axios from "axios";
import moment from "moment-timezone";
import "leaflet/dist/leaflet.css";

// Legend categories with full labels including values
const LEGEND_CATEGORIES = [
  { label: "No Risk (< 1 m)", text: "No Risk", max: 1, color: "rgb(148, 203, 85)" },
  { label: "Low (≥ 1 to ≤ 1.5 m)", text: "Low", max: 1.5, color: "rgb(243, 235, 18)" },
  { label: "Medium (> 1.5 to ≤ 2.0 m)", text: "Medium", max: 2.0, color: "rgb(243, 155, 58)" },
  { label: "High (> 2 m)", text: "High", max: Infinity, color: "rgb(230, 45, 58)" },
];

const OceanWave = () => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Utility function to get wave category text and color for the table
  const getWaveCategory = (waveHeight) => {
    if (waveHeight === null || isNaN(waveHeight)) {
      return { text: "N/A", color: "rgb(222,222,223)" };
    }
    const category = LEGEND_CATEGORIES.find(cat => waveHeight <= cat.max);
    return category ? { text: category.text, color: category.color } : { text: "N/A", color: "rgb(222,222,223)" };
  };

  useEffect(() => {
    const fetchOceanData = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `https://tl-osfas.rimes.int/api/weatherforecasts?sourceId=1`
        );
        
        if (Array.isArray(response.data.data)) {
          const filteredData = response.data.data.filter(
            (item) => item.parameter.id === 3
          );

          const uniqueLocations = filteredData.reduce((acc, item) => {
            if (!acc[item.location.id] || moment(item.valid_time).isAfter(acc[item.location.id].valid_time)) {
              acc[item.location.id] = {
                locationName: item.location.name,
                municipalityName: item.location.region,
                validTime: item.valid_time,
                value: item.value,
              };
            }
            return acc;
          }, {});

          const latestForecasts = Object.values(uniqueLocations);
          setTableData(latestForecasts);
        } else {
          console.error("API response.data is not an array:", response.data);
          setTableData([]);
        }
      } catch (error) {
        console.error("Error fetching ocean data:", error);
        setTableData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOceanData();
  }, []);

  const columns = useMemo(
    () => [
      {
        accessorKey: 'locationName',
        header: 'Forecast Location',
        size: 200,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filter by Location"
            value={(column.getFilterValue() ?? '')}
            onChange={e => column.setFilterValue(e.target.value)}
            sx={{ width: '100%' }}
            InputProps={{
              disableUnderline: true,
            }}
          />
        ),
      },
      {
        accessorKey: 'municipalityName',
        header: 'Municipality',
        size: 150,
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filter by M..."
            value={(column.getFilterValue() ?? '')}
            onChange={e => column.setFilterValue(e.target.value)}
            sx={{ width: '100%' }}
            InputProps={{
              disableUnderline: true,
            }}
          />
        ),
      },
      {
        accessorKey: 'validTime',
        header: 'Forecast Time (TLT)',
        size: 200,
        Cell: ({ cell }) => moment.utc(cell.getValue()).tz('Asia/Dili').format('YYYY-MM-DD HH:mm')
      },
      {
        accessorKey: 'value',
        header: 'Value in meter',
        size: 150,
        Cell: ({ cell }) => cell.getValue() !== null ? `${cell.getValue().toFixed(2)} m` : 'N/A'
      },
      {
        accessorKey: 'characteristics',
        header: 'Characteristics',
        size: 150,
        Cell: ({ row }) => {
          const category = getWaveCategory(row.original.value);
          return (
            <span
              style={{
                backgroundColor: category.color,
                padding: '2px 5px',
                borderRadius: '3px',
                display: 'inline-block',
                color: 'black',
                fontWeight: 'bold',
              }}
            >
              {category.text}
            </span>
          );
        },
        filterFn: 'includesString',
        Filter: ({ column }) => (
          <TextField
            variant="standard"
            placeholder="Filter by Char..."
            value={(column.getFilterValue() ?? '')}
            onChange={e => column.setFilterValue(e.target.value)}
            sx={{ width: '100%' }}
            InputProps={{
              disableUnderline: true,
            }}
          />
        ),
      },
    ],
    []
  );

  return (
    <div className="info-content-text">
        <div className="risk-legend">
          <div className="risk-legend-items">
            {LEGEND_CATEGORIES.map((category) => (
              <div key={category.label} className="risk-legend-item">
                <i
                  className="risk-color"
                  style={{ background: category.color, opacity: "0.8", border: "1px solid black" }}
                ></i>
                <span>{category.label}</span>
              </div>
            ))}
          </div>
        </div>

      <Box sx={{ p: 2, borderTop: '1px solid #ddd', overflow: 'auto', width: '100%' }}>
        <MaterialReactTable
          columns={columns}
          data={tableData}
          state={{ isLoading: loading }}
          enableColumnFilters
          enableSorting
          enablePagination
          paginationDisplayMode='pages'
          initialState={{
            pagination: { pageSize: 10 },
          }}
          renderTopToolbarCustomActions={() => (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                width: '100%',
                p: '0.5rem',
                gap: '1rem',
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                Ocean Wave Forecasts
              </Typography>
            </Box>
          )}
          muiTableContainerProps={{ sx: { maxHeight: '600px' } }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 'bold',
              backgroundColor: '#e0e0e0',
            },
          }}
          renderEmptyRowsFallback={({ table }) => (
            <Typography>
              {table.getState().isLoading ? "Loading ocean data..." : "No ocean data found."}
            </Typography>
          )}
        />
      </Box>
    </div>
  );
};

export default OceanWave;